import { z } from 'zod';
import {
  waitlistPreferencesSchema,
  waitlistPrioritySchema,
  WAITLIST_PRIORITIES,
} from '../appointment/types';

/**
 * Waitlist management (ADR-271). The add request lives with the appointment
 * types (`addToWaitlistRequestSchema`), since the voice agent posts it through
 * the appointment client.
 */

export const WAITLIST_STATUSES = [
  'active',
  'contacted',
  'offered',
  'booked',
  'expired',
  'cancelled',
  'removed',
] as const;
export const waitlistStatusSchema = z.enum(WAITLIST_STATUSES);
export type WaitlistStatus = z.infer<typeof waitlistStatusSchema>;

/** Still on the list (and so kept in the EHR). */
export const OPEN_WAITLIST_STATUS_NAMES: readonly WaitlistStatus[] = [
  'active',
  'contacted',
  'offered',
];

export const WAITLIST_EHR_SYNC_STATUSES = [
  'pending',
  'synced',
  'failed',
  'not_applicable',
] as const;
export const waitlistEhrSyncStatusSchema = z.enum(WAITLIST_EHR_SYNC_STATUSES);
export type WaitlistEhrSyncStatus = z.infer<typeof waitlistEhrSyncStatusSchema>;

const namedRefSchema = z.object({ id: z.string().uuid(), name: z.string() });

export const waitlistEntrySchema = z.object({
  id: z.string().uuid(),
  patient: z.object({
    id: z.string().uuid(),
    name: z.string(),
    dateOfBirth: z.string().nullable(),
  }),
  /** The booked appointment the patient wants sooner. */
  appointment: z
    .object({
      id: z.string().uuid(),
      startTime: z.string(),
      providerName: z.string().nullable(),
      locationName: z.string().nullable(),
    })
    .nullable(),
  appointmentType: namedRefSchema.nullable(),
  /** Null = any provider. */
  provider: namedRefSchema.nullable(),
  /** Null = any location. */
  location: namedRefSchema.nullable(),
  preferences: waitlistPreferencesSchema,
  priority: waitlistPrioritySchema,
  status: waitlistStatusSchema,
  notes: z.string().nullable(),
  ehr: z.object({
    waitlistId: z.string().nullable(),
    syncStatus: waitlistEhrSyncStatusSchema,
    error: z.string().nullable(),
    errorCode: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
    /** While queued: when the next push attempt is due (a failed attempt backs off). */
    nextAttemptAt: z.string().nullable(),
  }),
  contact: z.object({
    attempts: z.number().int(),
    lastContactedAt: z.string().nullable(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const listWaitlistQuerySchema = z.object({
  /** `open` (default) = active/contacted/offered; `closed` = the rest; `all`. */
  view: z.enum(['open', 'closed', 'all']).default('open'),
  providerId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  priority: z.enum(WAITLIST_PRIORITIES).optional(),
  patientId: z.string().uuid().optional(),
  /** Patient name contains (case-insensitive). */
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListWaitlistQuery = z.input<typeof listWaitlistQuerySchema>;

export const listWaitlistResponseSchema = z.object({
  items: z.array(waitlistEntrySchema),
  total: z.number().int(),
});
export type ListWaitlistResponse = z.infer<typeof listWaitlistResponseSchema>;

/**
 * Edit an entry. Only fields present change; `null` provider / location means
 * any. Setting `appointmentId` takes the appointment type from it.
 */
export const updateWaitlistRequestSchema = z.object({
  appointmentId: z.string().uuid().nullable().optional(),
  appointmentTypeId: z.string().uuid().nullable().optional(),
  providerId: z.string().uuid().nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  preferences: waitlistPreferencesSchema.optional(),
  priority: waitlistPrioritySchema.optional(),
  notes: z.string().max(1000).nullable().optional(),
});
export type UpdateWaitlistRequest = z.infer<typeof updateWaitlistRequestSchema>;

export const WAITLIST_REMOVAL_REASONS = ['no_longer_needed', 'booked_elsewhere', 'other'] as const;
export const removeFromWaitlistRequestSchema = z.object({
  reason: z.enum(WAITLIST_REMOVAL_REASONS),
});
export type RemoveFromWaitlistRequest = z.infer<typeof removeFromWaitlistRequestSchema>;

/** What a read of the EHR's list changed here. */
export const waitlistRefreshResponseSchema = z.object({
  listed: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  removed: z.number().int(),
  unresolved: z.number().int(),
});
export type WaitlistRefreshResponse = z.infer<typeof waitlistRefreshResponseSchema>;

/**
 * What re-reading one entry from the EHR found (`POST /api/waitlist/:id/refresh`):
 * `unchanged` (the EHR holds what we hold), `updated` (ours now matches the
 * EHR's copy), `removed` (the EHR no longer has it), `unresolved` (the EHR's
 * copy names something we can't match; ours left as is), `superseded` (the
 * entry changed here while it was read; nothing written).
 */
export const WAITLIST_ENTRY_REFRESH_OUTCOMES = [
  'unchanged',
  'updated',
  'removed',
  'unresolved',
  'superseded',
] as const;
export const waitlistEntryRefreshResponseSchema = z.object({
  outcome: z.enum(WAITLIST_ENTRY_REFRESH_OUTCOMES),
  entry: waitlistEntrySchema,
});
export type WaitlistEntryRefreshResponse = z.infer<typeof waitlistEntryRefreshResponseSchema>;
